function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useState, useCallback, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import PropTypes from 'prop-types';
import cn from "../../../utils/cn";
import { get, every, includes, isEmpty, isFunction, isNil, isNull, slice, some } from 'lodash';
import moment from 'moment';
import Icon from "../../Display/Icon/Icon";
import { getPreferredProp } from "../utils";
import { momentizeValue, convertToUnixTimestamp, validateDate, shouldDisableDay } from "../../utils/Dates";
import { getPropagationPath } from "../../../utils/Browser";
import Calendar from "../Calendar/Calendar";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const DurationField = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    label,
    id,
    className = '',
    disabled = false,
    hideLabel = false,
    hint = '',
    inline = false,
    labelClassName = '',
    name = '',
    placeholder = '',
    required = false,
    style = {
      container: {},
      dropdown: {},
      label: {},
      input: {},
      hint: {},
      error: {}
    },
    dateFormat = 'MM/DD/YYYY',
    valueFormat = convertToUnixTimestamp,
    isUtc = true,
    maxDate = null,
    minDate = null,
    shouldDisableDate = () => false,
    separator = '-',
    startField = {},
    endField = {},
    startFieldPath = 'startField',
    endFieldPath = 'endField'
  } = props;
  let element = useRef(null);
  const [state, setState] = useState(() => {
    const startValue = momentizeValue(getPreferredProp(props, 'value', startFieldPath), dateFormat, isUtc);
    const endValue = momentizeValue(getPreferredProp(props, 'value', endFieldPath), dateFormat, isUtc);
    return {
      isOpen: false,
      startValue,
      endValue,
      startInputValue: null,
      endInputValue: null,
      yearValue: null,
      year: startValue && startValue.isValid() ? startValue.year() : moment().year(),
      month: startValue && startValue.isValid() ? startValue.month() : moment().month(),
      showRequiredError: false
    };
  });
  const onButtonBlur = isOpen => {
    if (!isOpen && required) {
      setState(prevState => {
        if (!prevState.startValue || !prevState.endValue) {
          return _objectSpread(_objectSpread({}, prevState), {}, {
            showRequiredError: true
          });
        }
        ;
        return _objectSpread({}, prevState);
      });
    } else {
      setState(prevState => _objectSpread(_objectSpread({}, prevState), {}, {
        showRequiredError: false
      }));
    }
  };
  const handleClickOutside = useCallback(e => {
    if (!includes(getPropagationPath(e), element.current)) {
      setState(prevState => _objectSpread(_objectSpread({}, prevState), {}, {
        isOpen: false
      }));
      onButtonBlur(false);
      if (isFunction(getPreferredProp(props, 'onBlur'))) {
        getPreferredProp(props, 'onBlur')();
      }
    }
  }, []);
  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [state.isOpen, handleClickOutside]);
  const onCalendarClick = e => {
    if (typeof document === 'undefined') return;
    e.preventDefault();
    setState(prevState => _objectSpread(_objectSpread({}, prevState), {}, {
      isOpen: !state.isOpen
    }));
    onButtonBlur(!state.isOpen);
  };
  const onInputChange = (e, path) => {
    const value = get(e, 'target.value', '');
    setState(prevState => _objectSpread(_objectSpread({}, prevState), {}, {
      ["".concat(path, "InputValue")]: value
    }));
  };
  const onChange = () => {
    let startValue = state.startValue;
    let endValue = state.endValue;
    if (startValue && endValue && startValue.isAfter(endValue)) {
      startValue = state.endValue;
      endValue = state.startValue;
      setState(prevState => _objectSpread(_objectSpread({}, prevState), {}, {
        startValue,
        endValue
      }));
    }
    if (isUtc) {
      startValue = startValue && moment.utc(startValue.format(dateFormat), dateFormat);
      endValue = endValue && moment.utc(endValue.format(dateFormat), dateFormat);
    }
    if (isFunction(valueFormat)) {
      getPreferredProp(props, 'onChange', startFieldPath)(startValue ? valueFormat(startValue) : '');
      getPreferredProp(props, 'onChange', endFieldPath)(endValue ? valueFormat(endValue) : '');
    } else {
      getPreferredProp(props, 'onChange', startFieldPath)(startValue ? startValue.format(valueFormat) : '');
      getPreferredProp(props, 'onChange', endFieldPath)(endValue ? endValue.format(valueFormat) : '');
    }
  };
  const handleBlur = (e, path) => {
    if (e.target.value.length < 3) {
      setState(_objectSpread(_objectSpread({}, state), {}, {
        ["".concat(path, "InputValue")]: null
      }));
      return;
    }
    const oldValue = get(state, "".concat(path, "InputValue"), null);
    const fieldPath = get(props, "".concat(path, "FieldPath"), null);
    if (!isNull(oldValue)) {
      const value = momentizeValue(e.target.value, dateFormat, isUtc);
      setState(_objectSpread(_objectSpread({}, state), {}, {
        ["".concat(path, "InputValue")]: null,
        ["".concat(path, "Value")]: value,
        year: value && value.isValid() ? value.year() : moment().year(),
        month: value && value.isValid() ? value.month() : moment().month()
      }));
      onChange();
    }
    if (isFunction(getPreferredProp(props, 'onBlur', fieldPath))) {
      getPreferredProp(props, 'onBlur', fieldPath)();
    }
  };
  const onDaySelect = (day, secondary) => {
    const value = isUtc ? day.utc() : day;
    let {
      startValue,
      endValue,
      year,
      month
    } = state;
    let stayOpen = true;
    if (!secondary) {
      year = value && value.isValid() ? value.year() : moment().year();
      month = value && value.isValid() ? value.month() : moment().month();
    }
    if (startValue && !endValue) {
      endValue = day;
      stayOpen = false;
    } else {
      startValue = day;
      endValue = null;
    }
    setState(prevState => _objectSpread(_objectSpread({}, prevState), {}, {
      isOpen: stayOpen,
      startValue,
      endValue,
      year,
      month
    }));
  };
  useEffect(() => {
    if (state.startValue && state.endValue) {
      onChange();
    }
  }, [state.startValue, state.endValue]);
  const onPreviousMonth = () => {
    const prev = moment({
      month: state.month,
      year: state.year
    }).subtract(1, 'month');
    setState(prevState => _objectSpread(_objectSpread({}, prevState), {}, {
      month: prev.month(),
      year: prev.year()
    }));
  };
  const onNextMonth = () => {
    const next = moment({
      month: state.month,
      year: state.year
    }).add(1, 'month');
    setState(prevState => _objectSpread(_objectSpread({}, prevState), {}, {
      month: next.month(),
      year: next.year()
    }));
  };
  const onEditYear = e => {
    const value = slice(e.target.value, 0, 4).toString().replace(/\D/g, '');
    setState(prevState => _objectSpread(_objectSpread({}, prevState), {}, {
      yearValue: value
    }));
  };
  useEffect(() => {
    if (state.yearValue && state.yearValue.length === 4) {
      setState(prevState => _objectSpread(_objectSpread({}, prevState), {}, {
        year: parseInt(state.yearValue, 10),
        yearValue: null
      }));
    }
  }, [state.yearValue]);
  const onFakeInputKeyPress = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      onCalendarClick(e);
    }
  };
  const getInputValue = path => {
    const value = get(state, "".concat(path, "Value"), null);
    const inputValue = get(state, "".concat(path, "InputValue"), null);
    if (isNil(inputValue)) {
      if (value) {
        return value.format(dateFormat);
      }
      return '';
    }
    return inputValue;
  };
  const validate = value => {
    const date = momentizeValue(value, dateFormat, isUtc);
    return validateDate(date, maxDate, minDate, shouldDisableDate, dateFormat);
  };

  // Expose a validate method to the parent component
  useImperativeHandle(ref, () => ({
    validate: value => validate(value)
  }));
  const getFormattedRange = () => {
    const {
      startValue,
      endValue
    } = state;
    const startContent = startValue ? startValue.format(dateFormat) : 'mm/dd/yyyy';
    const endContent = endValue ? endValue.format(dateFormat) : 'mm/dd/yyyy';
    const start = /*#__PURE__*/_jsx("span", {
      className: "".concat(startValue && "text-text-blue", " -ml-8"),
      children: startContent
    }, "start");
    const end = /*#__PURE__*/_jsx("span", {
      className: endValue && "text-text-blue",
      children: endContent
    }, "end");
    const separatorSign = /*#__PURE__*/_jsxs("span", {
      className: (startValue || endValue) && 'text-text-blue',
      children: [" ", separator, " "]
    }, "separator");
    return [start, separatorSign, end];
  };
  const fields = [startField, endField];
  const fieldPaths = [startFieldPath, endFieldPath];
  const startError = getPreferredProp(props, 'error', startFieldPath);
  const endError = getPreferredProp(props, 'error', endFieldPath);
  const hasError = every(fields, (field, index) => getPreferredProp(props, 'touched', fieldPaths[index])) && (some(fields, (field, index) => getPreferredProp(props, 'invalid', fieldPaths[index])) || state.startValue && !state.startValue.isValid() || state.endValue && !state.endValue.isValid());
  const nextMonth = moment({
    year: state.year,
    month: state.month
  }).add(1, 'month');
  const containerClass = () => cn('mb-3 font-regular-font tracking-wide leading-4', {
    'flex': inline,
    'text-red': hasError || state.showRequiredError,
    'text-dark-grey': !hasError && !state.showRequiredError
  }, className);
  const buttonClass = () => cn('h-38px w-full pr-9 border border-solid rounded lowercase text-medium-grey text-sm', {
    'group-hover:bg-medium-fill-grey cursor-pointer': !disabled,
    'focus:border-focus-blue': true,
    'border-light-border-blue': !state.isOpen && !hasError && !state.showRequiredError,
    'border-action-blue': state.isOpen && !hasError && !state.showRequiredError,
    'border-red': hasError || state.showRequiredError,
    'cursor-default': disabled
  });
  const labelClass = () => cn({
    'font-heavy-font text-xs': true,
    'pt-3 pr-4': inline,
    [labelClassName]: !isEmpty(labelClassName),
    'app-components-form-field__label--required': required,
    'sr-only': hideLabel
  });
  const dropDownClass = () => cn('absolute z-50 bg-white border border-solid rounded border-action-blue mt-2 w-500', {
    'hidden': !state.isOpen,
    'block': state.isOpen
  });
  const inputClass = type => cn('w-1/2 h-38px pl-2 lowercase placeholder-medium-grey text-sm', {
    'border border-solid rounded border-light-border-blue': true,
    'text-text-blue': state["".concat(type, "Value")],
    'text-medium-grey': !state["".concat(type, "Value")]
  });
  return /*#__PURE__*/_jsxs("div", {
    className: containerClass(),
    style: style.container,
    ref: element,
    children: [/*#__PURE__*/_jsx("label", {
      htmlFor: id,
      className: labelClass(),
      style: style.label,
      children: label
    }), /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsxs("div", {
        className: "relative group w-64 mt-1",
        children: [/*#__PURE__*/_jsx("button", {
          id: id,
          className: buttonClass(),
          onClick: onCalendarClick,
          disabled: disabled,
          onKeyDown: onFakeInputKeyPress,
          onBlur: () => onButtonBlur(state.isOpen),
          children: getFormattedRange()
        }), /*#__PURE__*/_jsx("div", {
          className: "absolute top-0 right-0 p-2.5",
          children: /*#__PURE__*/_jsx(Icon, {
            icon: "Calendar",
            disabled: disabled || state.startValue && !state.startValue.isValid(),
            onClick: onCalendarClick,
            color: "medium-grey"
          })
        })]
      }), /*#__PURE__*/_jsxs("div", {
        className: dropDownClass(),
        style: style.dropdown,
        children: [/*#__PURE__*/_jsxs("div", {
          className: "flex p-4 gap-4",
          children: [/*#__PURE__*/_jsx("label", {
            htmlFor: "".concat(id, "-start"),
            className: "sr-only",
            children: "Start Date"
          }), /*#__PURE__*/_jsx("input", {
            id: "".concat(id, "-start"),
            value: getInputValue('start'),
            onChange: e => onInputChange(e, 'start'),
            onBlur: e => handleBlur(e, 'start'),
            onFocus: getPreferredProp(props, 'onFocus', startFieldPath),
            placeholder: placeholder || dateFormat,
            name: "".concat(name, "-start"),
            disabled: disabled,
            type: "text",
            style: style.input,
            className: inputClass('start')
          }), /*#__PURE__*/_jsx("label", {
            htmlFor: "".concat(id, "-end"),
            className: "sr-only",
            children: "End Date"
          }), /*#__PURE__*/_jsx("input", {
            id: "".concat(id, "-end"),
            value: getInputValue('end'),
            onChange: e => onInputChange(e, 'end'),
            onBlur: e => handleBlur(e, 'end'),
            onFocus: getPreferredProp(props, 'onFocus', endFieldPath),
            placeholder: placeholder || dateFormat,
            name: "".concat(name, "-end"),
            disabled: disabled,
            type: "text",
            style: style.input,
            className: inputClass('end')
          })]
        }), /*#__PURE__*/_jsxs("div", {
          className: "flex justify-center px-4 font-heavy-font text-sm text-text-blue",
          children: [/*#__PURE__*/_jsx(Icon, {
            icon: "ChevronArrow",
            color: "action-blue",
            onClick: onPreviousMonth,
            direction: "left",
            ariaLabel: "Previous Month: ".concat(moment({
              month: state.month,
              year: state.year
            }).subtract(1, 'month').format('MMMM YYYY'))
          }), /*#__PURE__*/_jsxs("div", {
            className: "flex w-1/2 justify-center",
            children: [/*#__PURE__*/_jsxs("span", {
              children: [moment({
                month: state.month,
                year: state.year
              }).format('MMMM'), "\xA0"]
            }), /*#__PURE__*/_jsx("label", {
              htmlFor: "".concat(id, "-start-year"),
              className: "sr-only",
              children: "Start Year"
            }), /*#__PURE__*/_jsx("input", {
              className: "font-heavy-font w-10",
              id: "".concat(id, "-start-year"),
              value: isNull(state.yearValue) ? state.year : state.yearValue,
              onChange: onEditYear
            })]
          }), /*#__PURE__*/_jsxs("div", {
            className: "flex w-1/2 justify-center",
            children: [/*#__PURE__*/_jsxs("span", {
              children: [moment({
                month: nextMonth.month(),
                year: nextMonth.year()
              }).format('MMMM'), "\xA0"]
            }), /*#__PURE__*/_jsx("label", {
              htmlFor: "".concat(id, "-end-year"),
              className: "sr-only",
              children: "End Year"
            }), /*#__PURE__*/_jsx("input", {
              className: "font-heavy-font w-10",
              id: "".concat(id, "-end-year"),
              value: isNull(state.yearValue) ? nextMonth.year() : state.yearValue,
              onChange: onEditYear
            })]
          }), /*#__PURE__*/_jsx(Icon, {
            icon: "ChevronArrow",
            color: "action-blue",
            onClick: onNextMonth,
            direction: "right",
            ariaLabel: "Next Month: ".concat(moment({
              month: state.month,
              year: state.year
            }).add(2, 'month').format('MMMM YYYY'))
          })]
        }), /*#__PURE__*/_jsxs("div", {
          className: "flex p-4 justify-between",
          children: [/*#__PURE__*/_jsx(Calendar, {
            year: state.year,
            month: state.month,
            onDayClick: onDaySelect,
            dateRange: {
              start: state.startValue,
              end: state.endValue
            },
            isUtc: isUtc,
            shouldDisableDay: day => shouldDisableDay(day, maxDate, minDate, shouldDisableDate)
          }), /*#__PURE__*/_jsx(Calendar, {
            year: nextMonth.year(),
            month: nextMonth.month(),
            onDayClick: day => onDaySelect(day, true),
            dateRange: {
              start: state.startValue,
              end: state.endValue
            },
            isUtc: isUtc,
            shouldDisableDay: day => shouldDisableDay(day, maxDate, minDate, shouldDisableDate)
          })]
        })]
      }), !state.isOpen && hint && /*#__PURE__*/_jsx("div", {
        className: "text-xs italic mt-1 text-dark-grey",
        style: style.hint,
        children: /*#__PURE__*/_jsx("p", {
          children: hint
        })
      }), !state.isOpen && /*#__PURE__*/_jsxs("div", {
        className: "text-red text-xs",
        style: style.error,
        children: [startError && /*#__PURE__*/_jsx("p", {
          children: startError
        }), endError && /*#__PURE__*/_jsx("p", {
          children: endError
        }), state.showRequiredError && /*#__PURE__*/_jsx("p", {
          children: "Required"
        })]
      })]
    })]
  });
});
DurationField.propTypes = {
  /** id for reference */
  id: PropTypes.string.isRequired,
  /** className for reference */
  className: PropTypes.string,
  labelClassName: PropTypes.string,
  /** Override the inline-styles of the elements || **Not Recommended** */
  style: PropTypes.shape({
    container: PropTypes.object,
    dropdown: PropTypes.object,
    label: PropTypes.object,
    input: PropTypes.object,
    hint: PropTypes.object,
    error: PropTypes.object
  }),
  /** label text for button */
  label: PropTypes.string.isRequired,
  /** Input name */
  name: PropTypes.string,
  /** Should the label be displayed inline with the input? */
  inline: PropTypes.bool,
  /** Is the input disabled? */
  disabled: PropTypes.bool,
  /** Placeholder text */
  placeholder: PropTypes.string,
  /** Should the label be hidden? */
  hideLabel: PropTypes.bool,
  /** Help text displayed below the input */
  hint: PropTypes.node,
  /** Is the field required */
  required: PropTypes.bool,
  /** How the date should be formated in the input */
  dateFormat: PropTypes.string,
  /** String between the two Dates */
  separator: PropTypes.string,
  /**
   * Specify the value format you want
   * @param date:moment
   */
  valueFormat: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  /** Should the component work with UTC moment? */
  isUtc: PropTypes.bool,
  /** Max permitted date */
  maxDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /** Min permitted date */
  minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /**
   * Should this date be disabled?
   * @param date:moment
   */
  shouldDisableDate: PropTypes.func,
  /** See fieldProps doc */
  startField: PropTypes.object,
  /** See fieldProps doc */
  endField: PropTypes.object,
  /** if the Field is on a different path than props.startField */
  startFieldPath: PropTypes.string,
  /** if the Field is on a different path than props.endField */
  endFieldPath: PropTypes.string
};
DurationField.displayName = 'DurationField';
export default DurationField;
//# sourceMappingURL=DurationField.js.map